import {
  action,
  makeObservable,
  observable,
  reaction,
  runInAction
} from 'mobx';

import {
  mapWzOAuthErrorToMessage,
  toWzOAuthError,
  WzOAuthClientError,
  WzOAuthError,
  WzOAuthErrorCode
} from 'shared/entities/channels';
import { apiUrls } from 'shared/entities/domain';
import { IRootStore } from 'shared/entities/store/rootStore';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { PollingWindowModel } from 'shared/models/polling';
import { ReactionsHandlerStore } from 'stores/reactionsHandlerStore';

const GET_URL_TIMEOUT_MS = 15000;
const POPUP_POLL_ATTEMPTS = 900;
const POPUP_RESULT_GRACE_POLLS = 3;
const EXPECTED_ERROR_CODES = Object.values(WzOAuthError);

type CallbackResult =
  | { ok: true; channelId: string | null }
  | { ok: false; errorCode: WzOAuthError };

/** Starts OAuth in a popup and applies the result the callback page posts before the window closes. */
export class WazzupOAuthConnectionStore extends ReactionsHandlerStore {
  readonly authorizationStage = new LoadingStageModel();
  errorCode: WzOAuthErrorCode | null = null;
  fallbackUrl: string | null = null;
  statusUnavailable = false;
  targetChannelId: string | null = null;

  private readonly _rootStore: IRootStore;
  private readonly _popup: PollingWindowModel;
  private readonly _onSuccess: (channelId: string) => void;
  private _generation = 0;
  private _projectId: string | null = null;
  private _abortController: AbortController | null = null;
  private _callbackResult: CallbackResult | null = null;
  private _callbackOrigin: string | null = null;
  private _popupWindow: Window | null = null;
  private _closedPopupChecks = 0;

  constructor(rootStore: IRootStore, onSuccess: (channelId: string) => void) {
    super();
    this._rootStore = rootStore;
    this._onSuccess = onSuccess;
    this._popup = new PollingWindowModel({
      rootStore,
      cb: this._checkPopup,
      showTimeoutNotification: false
    });

    this.addReaction({
      key: 'resetConnectionOnProjectChange',
      reaction: reaction(
        () => this._rootStore.projectId,
        () => this._resetConnection()
      )
    });

    makeObservable(this, {
      connectChannel: action,
      errorCode: observable,
      fallbackUrl: observable,
      statusUnavailable: observable,
      targetChannelId: observable
    });
    window.addEventListener('message', this._onCallbackMessage);
  }

  connectChannel = async (channelId: string | null = null): Promise<void> => {
    if (this.authorizationStage.isLoading) {
      return;
    }

    const generation = ++this._generation;
    this._projectId = this._rootStore.projectId;
    this._abortController?.abort();
    this._abortController = new AbortController();
    this.targetChannelId = channelId;
    this._callbackResult = null;
    this._callbackOrigin = null;
    this._closedPopupChecks = 0;
    this.errorCode = null;
    this.fallbackUrl = null;
    this.statusUnavailable = false;
    this.authorizationStage.loading();

    const opened = this._popup.openEmptyWindow();
    this._popupWindow = this._popup.openedWindowReference;
    if (!opened) {
      this._fail(WzOAuthClientError.popupBlocked, generation);
      return;
    }

    const response = await this._rootStore.networkStore.api<
      { url: string },
      WzOAuthError
    >(apiUrls.CHANNELS_WZ_OAUTH_GET_URL, {
      method: 'GET',
      data: channelId ? { channel_id: channelId } : {},
      config: {
        signal: this._abortController.signal,
        timeout: GET_URL_TIMEOUT_MS
      },
      expectedErrorCodes: EXPECTED_ERROR_CODES,
      errorsMap: mapWzOAuthErrorToMessage
    });

    if (!this._isCurrent(generation)) {
      return;
    }

    if (response.isError) {
      this._popup.closeOpenedWindow();
      const code = response.data?.code;
      if (code === WzOAuthError.channelNotFound) {
        await this._rootStore.channelsStore.refresh();
      } else if (code === WzOAuthError.disabled && this._projectId) {
        await this._rootStore.projectStore.load(this._projectId);
      }

      this._fail(code as WzOAuthErrorCode | undefined, generation);
      return;
    }

    try {
      const redirectUri = new URL(response.data.url).searchParams.get(
        'redirect_uri'
      );
      if (!redirectUri) {
        this._fail(WzOAuthError.oauthFailed, generation);
        return;
      }

      this._callbackOrigin = new URL(redirectUri).origin;
    } catch {
      this._fail(WzOAuthError.oauthFailed, generation);
      return;
    }

    if (!this._popup.setWindowUrl(response.data.url)) {
      this.fallbackUrl = response.data.url;
      this._fail(WzOAuthClientError.popupBlocked, generation);
      return;
    }

    void this._popup.startPolling(POPUP_POLL_ATTEMPTS).then(({ isError }) => {
      if (
        isError &&
        this._isCurrent(generation) &&
        this.authorizationStage.isLoading
      ) {
        this._fail(WzOAuthClientError.resultUnknown, generation);
      }
    });
  };

  stopWaiting = (): void => {
    ++this._generation;
    this._abortController?.abort();
    this._popup.stopPolling();
    this._popup.closeOpenedWindow();
    this.authorizationStage.reset();
    runInAction(() => {
      this.statusUnavailable = false;
      this.fallbackUrl = null;
    });
  };

  dispose = (): void => {
    this.disposeAll();
    this._resetConnection();
    this._popup.closePollingBanner();
    window.removeEventListener('message', this._onCallbackMessage);
  };

  private _isCurrent(generation: number): boolean {
    return (
      generation === this._generation &&
      this._rootStore.projectId === this._projectId
    );
  }

  private _resetConnection(): void {
    this.stopWaiting();
    runInAction(() => {
      this.targetChannelId = null;
      this.errorCode = null;
    });
  }

  private _onCallbackMessage = (event: MessageEvent): void => {
    if (
      event.origin !== this._callbackOrigin ||
      event.source !== this._popupWindow ||
      event.data?.type !== 'spro-wz-oauth-result' ||
      typeof event.data.ok !== 'boolean'
    ) {
      return;
    }

    const { ok, channel_id: channelId, error_code: errorCode } = event.data;
    this._callbackResult = ok
      ? { ok, channelId: typeof channelId === 'string' ? channelId : null }
      : {
          ok,
          errorCode: toWzOAuthError(errorCode) ?? WzOAuthError.oauthFailed
        };
  };

  private _checkPopup = async (): Promise<BaseResponse> => {
    if (this._popup.hasOpenedWindow) {
      return { isError: true };
    }

    const result = this._callbackResult;
    if (result === null) {
      if (this._closedPopupChecks < POPUP_RESULT_GRACE_POLLS) {
        this._closedPopupChecks++;
        return { isError: true };
      }

      // Страница Wazzup могла оборвать связь с окном (COOP) — результата нет,
      // но канал мог подключиться, поэтому обновляем список перед сообщением
      const generation = this._generation;
      await this._rootStore.channelsStore.refresh();
      this._fail(WzOAuthClientError.resultUnknown, generation);
      return { isError: false };
    }

    if (!result.ok) {
      this._fail(result.errorCode, this._generation);
      return { isError: false };
    }

    const generation = this._generation;
    const isNewChannel = this.targetChannelId === null;
    const response = await this._rootStore.channelsStore.refresh();
    if (!this._isCurrent(generation)) {
      return { isError: false };
    }

    if (response.isError) {
      this._fail(WzOAuthClientError.resultUnknown, generation);
      return { isError: false };
    }

    const channelId = result.channelId;
    const channel = channelId
      ? this._rootStore.channelsStore.channels.getEntity(channelId)
      : null;
    if (!channelId || !channel) {
      this._fail(WzOAuthClientError.resultUnknown, generation);
      return { isError: false };
    }

    this.targetChannelId = null;
    this.errorCode = null;
    this.authorizationStage.success();
    if (isNewChannel) {
      try {
        this._rootStore.channelsStore.notifyChannelCreated(channel);
      } catch {
        // Ошибка уведомления не должна мешать выбору уже созданного канала.
      }
    }
    this._onSuccess(channelId);
    return { isError: false };
  };

  private _fail(code: WzOAuthErrorCode | undefined, generation: number): void {
    if (!this._isCurrent(generation)) {
      return;
    }

    this._popup.stopPolling();
    this._popup.closeOpenedWindow();
    runInAction(() => {
      this.errorCode = code ?? WzOAuthError.oauthFailed;
      this.statusUnavailable = false;
    });
    this.authorizationStage.error();
  }
}
